/**
 * StepCategoryBadge (Sprint 4.3.5).
 *
 * Pill-shaped badge that classifies a Step node on the Pathway Studio
 * canvas. Mirrors the Awell Studio canvas reference (downloaded
 * screenshot `Home_Visual-p-1080.png`) where each step card surfaces a
 * coloured category pill (BASELINE / 6 WEEK PROGRAM / EXCLUSION ...).
 *
 * The Awell colour palette is NOT copied verbatim. Per HC2 every accent
 * resolves through Signal DS tokens defined in `src/styles/tokens.ts`.
 * The 6 categories map to the closest semantic palette already in DS:
 *   - baseline    -> warning-strong (orange/red, high salience)
 *   - program     -> signal-primary (brand blue)
 *   - exclusion   -> surface-muted   (neutral, low salience)
 *   - completion  -> status-active   (green)
 *   - review      -> warning-soft    (amber)
 *   - other       -> surface-muted   (neutral)
 *
 * Anchored to the canonical Scrips prototype:
 *   https://ai-native-prototype.vercel.app/care-pathway-studio.html
 *   (canvas section absent there; Awell screenshot is the canvas-specific
 *   visual source of truth per Plan v9 §4.2 prototype-anchoring rule).
 *
 * HC2 token-only. HC5 zero em / en dashes. HC6 a11y: `role="status"` +
 * `aria-label="Step category: <label>"`. Brand-Patch v2 mandatory: zero
 * `\bTLP[1-4]\b` user-facing strings; the `label` prop is rendered as
 * provided so callers must not pass TLP1-4 verbatim.
 */
import {
  STEP_CATEGORIES,
  type StepCategory,
} from '../../services/pathway-layout';

export { STEP_CATEGORIES };
export type { StepCategory };

export interface StepCategoryBadgeProps {
  /** Closed-vocab step category. */
  category: StepCategory;
  /**
   * Display text for the pill (e.g. "BASELINE", "16 Week Program",
   * "Exclusion"). Rendered uppercase via `uppercase` Tailwind token.
   */
  label: string;
  /**
   * Optional duration text appended to the label for `program` category
   * (e.g. "6 Week Program"). When `category !== 'program'` this prop is
   * ignored.
   */
  programDuration?: string;
  /** Optional anchor id to disambiguate testIds when many badges render. */
  anchorId?: string;
  className?: string;
}

/**
 * Token classes per category. Ordered by category vocab so the audit
 * pass can grep for missing entries.
 */
const TOKEN_CLASSES: Record<StepCategory, string> = {
  baseline:
    'bg-warning-strong-bg text-warning-strong-fg ring-warning-strong-border',
  program:
    'bg-signal-primary-100 text-signal-primary-700 ring-signal-primary-300',
  exclusion: 'bg-surface-muted text-text-secondary ring-surface-border',
  completion: 'bg-status-active-bg text-status-active-fg ring-status-active',
  review: 'bg-warning-soft-bg text-warning-soft-fg ring-warning-soft-border',
  other: 'bg-surface-muted text-text-tertiary ring-surface-border',
};

export function StepCategoryBadge({
  category,
  label,
  programDuration,
  anchorId,
  className,
}: StepCategoryBadgeProps) {
  const testId = `step-category-badge-${anchorId ?? category}`;
  const display =
    category === 'program' && programDuration ? programDuration : label;
  return (
    <span
      role="status"
      aria-label={`Step category: ${display}`}
      data-testid={testId}
      data-step-category={category}
      className={[
        'inline-flex items-center rounded-signal-pill px-gutter-md py-0.5 text-signal-xs font-semibold uppercase tracking-wider ring-1 ring-inset',
        TOKEN_CLASSES[category],
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {display}
    </span>
  );
}
